import React, { createContext, useContext, useEffect, useState } from 'react';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { collectionGroup, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase'; 
import { ROLES } from '../config/permissions';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null); 
    const [dynamicServerId, setDynamicServerId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSupportManager, setIsSupportManager] = useState(false);

    const fetchUserPermissions = async (user) => {
        if (!user) {
            setUserRole(null);
            setDynamicServerId(null);
            return;
        }

        try {
            // 2. Global Users Check (SSO Architecture)
            const globalUsersRef = collection(db, 'users');
            const savedUsername = localStorage.getItem('loginUsername');
            
            let globalQuery;
            if (savedUsername) {
                // If we know exactly who they logged in as, strictly use that!
                globalQuery = query(globalUsersRef, where('username', '==', savedUsername));
            } else {
                // Fallback for page reloads where localStorage might be cleared
                globalQuery = query(globalUsersRef, where('uid', '==', user.uid));
            }
            
            const globalSnap = await getDocs(globalQuery);

            if (!globalSnap.empty) {
                // If there are multiple (because of shared UIDs), find the exact username match
                let targetDoc = globalSnap.docs[0];
                if (!savedUsername) {
                    targetDoc = globalSnap.docs.find(d => d.data().username === savedUsername) || globalSnap.docs[0];
                }

                const data = targetDoc.data();
                
                // Prioritize permissionLevel
                const resolvedRole = data.permissionLevel || (data.role === 'Global Admin' ? ROLES.SUPPORT : null);
                
                setUserRole(resolvedRole);
                setDynamicServerId(null);
                setIsSupportManager(data.isSupportManager === true);
                
                return; 
            }

            // 3. Local Admins Check (Targeted routing)
            const targetLoginServer = localStorage.getItem('targetLoginServer');
            let adminsQuery;
            
            if (targetLoginServer) {
                // If they requested a specific server on the login page, check that server explicitly
                adminsQuery = query(collection(db, 'servers', targetLoginServer, 'admins'), where('uid', '==', user.uid));
            } else {
                // Fallback: Check everywhere if they didn't specify
                adminsQuery = query(collectionGroup(db, 'admins'), where('uid', '==', user.uid));
            }
            
            let querySnapshot = await getDocs(adminsQuery);

            // Fallback if target server check failed but they exist somewhere else
            if (querySnapshot.empty && targetLoginServer) {
                adminsQuery = query(collectionGroup(db, 'admins'), where('uid', '==', user.uid));
                querySnapshot = await getDocs(adminsQuery);
            }

            if (!querySnapshot.empty) {
                const adminDoc = querySnapshot.docs[0];
                const data = adminDoc.data();
                
                const pLevel = Array.isArray(data.permissionLevel) ? data.permissionLevel : (data.permissionLevel ? [data.permissionLevel] : []);
                const pLevels = Array.isArray(data.permissionLevels) ? data.permissionLevels : (data.permissionLevels ? [data.permissionLevels] : []);
                const roles = [...pLevel, ...pLevels];

                if (roles.includes(ROLES.SERVER_SUPER_ADMIN) || roles.includes('Super Admin')) {
                    setUserRole(ROLES.SERVER_SUPER_ADMIN);
                } else {
                    setUserRole(ROLES.ADMIN); 
                }

                const realServerId = adminDoc.ref.parent.parent.id;
                setDynamicServerId(realServerId);
                
                // Clear the routing request so it doesn't pollute future reloads
                localStorage.removeItem('targetLoginServer');
                return;
            }

            // 4. No roles found
            setUserRole(null);
            setDynamicServerId(null);

        } catch (error) {
            console.error("Error fetching permissions:", error);
            setUserRole(null);
            setDynamicServerId(null);
            setIsSupportManager(false);
        }
    };

    async function login(email, password) {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        await fetchUserPermissions(user);
        return user;
    }

    function logout() {
        setUserRole(null);
        setDynamicServerId(null);
        localStorage.removeItem('targetLoginServer');
        return signOut(auth);
    }

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            if (user) {
                await fetchUserPermissions(user);
            } else {
                setUserRole(null);
                setDynamicServerId(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = { currentUser, userRole, dynamicServerId, isSupportManager, login, logout };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}